/**
 * Amplitude Analytics Service for Vivideo
 *
 * Parallel, independent tracking system to Google Analytics. Uses Amplitude's
 * "Object Verb" event taxonomy and exposes typed helper functions for every
 * tracked event so callers never have to hand-write event names.
 *
 * Configured for EVENTS-ONLY (no autocapture) + Session Replay @ 100%.
 * Anonymous sessions are captured via Amplitude's auto-generated device_id
 * (persisted in localStorage) and auto-stitched to the backend user_id on
 * login via identifyAmplitudeUser().
 */

import {
  init,
  track,
  identify,
  reset,
  setUserId,
  add,
  revenue as amplitudeRevenue,
  Identify,
  Revenue,
} from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import i18n from '@/i18n';
import { approvedAtMs } from '@/v2/videoRating';

// ============================================
// CONFIG
// ============================================

// Session Replay sample rate. Starts at 100% so we capture everything during
// rollout; tune down once volume is understood.
export const SESSION_REPLAY_SAMPLE_RATE = 1.0;

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) || '2.0.0';
const ENVIRONMENT =
  (import.meta.env.MODE as string | undefined) === 'production' ? 'production' : 'development';

let initialized = false;

// ============================================
// A/B PROVIDER NAME
// ============================================

/**
 * The three clean names the A/B dashboard groups by — never an internal code.
 * LongVid and VideoGen orders always stamp their provider, so anything else
 * (including an unstamped order) is HeyGen.
 */
export function analyticsProviderName(value?: string | null): 'heygen' | 'longvid' | 'videogen' {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'longvid' || v.startsWith('longvid_')) return 'longvid';
  if (v === 'videogen' || v.startsWith('videogen_')) return 'videogen';
  return 'heygen';
}

/**
 * A LongVid / VideoGen order that HeyGen rendered in the provider's place carries
 * `fallback_from` (set on the order by the backend). Normalised to the same clean
 * names, so a dashboard can keep such videos out of the real HeyGen arm; undefined
 * when the video was made by the provider it was routed to.
 */
export function analyticsFallbackFrom(value?: string | null): 'longvid' | 'videogen' | undefined {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return undefined;
  if (v === 'longvid' || v.startsWith('longvid_')) return 'longvid';
  if (v === 'videogen' || v.startsWith('videogen_')) return 'videogen';
  return undefined;
}

/** The video's approval time (order `approved_at`) as epoch seconds + its age in hours,
 *  so the A/B can drop pre-test videos; both undefined when the time is unknown. */
export function analyticsVideoTime(approvedAt?: string | null, nowMs: number = Date.now()): { video_approved_ts?: number; video_age_h?: number } {
  const ms = approvedAtMs(approvedAt);
  if (ms === null) return { video_approved_ts: undefined, video_age_h: undefined };
  return { video_approved_ts: Math.floor(ms / 1000), video_age_h: Math.round((nowMs - ms) / 360_000) / 10 };
}

// ============================================
// INIT
// ============================================

/**
 * Cross-domain handoff from the marketing site (vivideo.ai). The landing page
 * appends its Amplitude device_id + attribution to CTA links so the SAME
 * device stream continues here (identityStorage is localStorage, which does
 * NOT cross subdomains — the URL param is the bridge). Parsed once at init;
 * the params are then scrubbed from the URL so they don't leak into routing,
 * copied links, or page_view paths.
 */
const LANDING_PARAMS = ['amp_device_id', 'vv_cta', 'utm_source', 'utm_medium', 'utm_campaign'] as const;

const readLandingHandoff = () => {
  try {
    const qs = new URLSearchParams(window.location.search);
    const raw = {
      deviceId: qs.get('amp_device_id') || undefined,
      cta: qs.get('vv_cta') || undefined,
      utmSource: qs.get('utm_source') || undefined,
      utmMedium: qs.get('utm_medium') || undefined,
      utmCampaign: qs.get('utm_campaign') || undefined,
    };
    // Only accept a sane device id (Amplitude ids are UUID-like) — never let a
    // crafted link inject junk identity.
    const deviceId = raw.deviceId && /^[A-Za-z0-9-]{10,64}$/.test(raw.deviceId) ? raw.deviceId : undefined;
    const hasAny = Object.values(raw).some(Boolean);
    if (hasAny) {
      // Scrub ONLY our params — OAuth/Stripe flows rely on their own query params.
      const url = new URL(window.location.href);
      LANDING_PARAMS.forEach((p) => url.searchParams.delete(p));
      window.history.replaceState(window.history.state, '', url.toString());
    }
    return { ...raw, deviceId };
  } catch {
    return { deviceId: undefined, cta: undefined, utmSource: undefined, utmMedium: undefined, utmCampaign: undefined };
  }
};

export const initAmplitude = () => {
  if (initialized) return;

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined;
  if (!apiKey) {
    console.warn('[Amplitude] VITE_AMPLITUDE_API_KEY is not set — Amplitude disabled');
    return;
  }

  const handoff = readLandingHandoff();

  try {
    // IMPORTANT: per Amplitude docs, `add(sessionReplayPlugin())` must be
    // called BEFORE `init(API_KEY)`. Otherwise the plugin starts with its
    // own fallback device_id before the core SDK has loaded the persisted
    // one from localStorage, which shows up in Amplitude's setup validator
    // as "Device ID mismatch detected in Session Replay plugin configuration".
    //
    // Privacy:
    //   - `defaultMaskLevel: 'medium'` masks all form inputs (email, text,
    //     textareas) + any element with `data-amp-mask` out of the box.
    //   - `maskSelector` adds extra belt-and-suspenders coverage for sensitive
    //     fields that might render outside a <form>.
    //   - `blockSelector` fully blacks out Stripe / payment iframes (iframes
    //     are already sandboxed by rrweb but this guarantees no screenshotting
    //     of rendered card-form wrappers either).
    //   - Anything explicitly marked `data-amp-unmask` is still captured —
    //     useful for things like credit balance, plan name, and other non-PII
    //     labels the product team wants visible in replays.
    add(
      sessionReplayPlugin({
        sampleRate: SESSION_REPLAY_SAMPLE_RATE,
        privacyConfig: {
          defaultMaskLevel: 'medium',
          maskSelector: [
            '[data-amp-mask]',
            'input[type="email"]',
            'input[type="password"]',
            'input[name="email"]',
            'input[name="cardnumber"]',
            '[data-sensitive]',
          ],
          blockSelector: [
            '[data-amp-block]',
            'iframe[src*="stripe"]',
            'iframe[src*="checkout.stripe.com"]',
          ],
          unmaskSelector: ['[data-amp-unmask]'],
        },
        captureDocumentTitle: true,
        // SPA fallback: react-router's navigate() goes through History API
        // which rrweb already patches, but polling catches edge cases like
        // programmatic `location.replace` in auth / oauth-callback flows.
        enableUrlChangePolling: true,
      })
    );

    init(apiKey, {
      // Events-only: no autocapture of page views / clicks / form submits etc.
      // We fire every event explicitly so the taxonomy stays clean.
      autocapture: false,
      defaultTracking: false,
      identityStorage: 'localStorage',
      // Continue the landing-page device stream when the CTA handed one over
      // (first visit only — once localStorage has an identity, it wins on
      // later visits because we only pass this on the initial init).
      ...(handoff.deviceId ? { deviceId: handoff.deviceId } : {}),
    });

    initialized = true;

    // First-touch attribution — setOnce so the FIRST arrival wins, ever.
    // (defaultTracking is off, so Amplitude's own UTM capture is disabled;
    // this is the explicit replacement.)
    if (handoff.cta || handoff.utmSource || handoff.utmMedium || handoff.utmCampaign || document.referrer) {
      const first = new Identify();
      if (handoff.utmSource) first.setOnce('initial_utm_source', handoff.utmSource);
      if (handoff.utmMedium) first.setOnce('initial_utm_medium', handoff.utmMedium);
      if (handoff.utmCampaign) first.setOnce('initial_utm_campaign', handoff.utmCampaign);
      if (handoff.cta) first.setOnce('initial_cta', handoff.cta);
      if (document.referrer) first.setOnce('initial_referrer', document.referrer);
      identify(first);
    }
  } catch (e) {
    console.error('[Amplitude] init failed:', e);
  }
};

// ============================================
// IDENTITY
// ============================================

interface AmplitudeUserContext {
  vivideo_user_id: string;
  user_email?: string;
  user_plan?: string;
  credits_remaining?: number;
  is_pro?: boolean;
  /**
   * Backend-assigned A/B bucket (0-15, `int(user_id, 16) % 16`; the V2 redesign
   * cohort is groups 14-15). Sent as a user property so every event and session
   * replay is attributable to an experiment cohort in Amplitude.
   */
  ab_group?: number;
  /** The user's CURRENT A/B arm (raw `video_provider` from get-user-info). */
  video_provider?: string | null;
}

/**
 * Bind the current Amplitude device_id to the backend user_id and set
 * identity-level user properties. Called from setAnalyticsUserContext() on
 * login / refresh so call sites stay unchanged.
 */
export const identifyAmplitudeUser = (ctx: AmplitudeUserContext) => {
  if (!initialized) return;
  try {
    setUserId(ctx.vivideo_user_id);

    const id = new Identify();
    if (ctx.user_plan !== undefined) id.set('user_plan', ctx.user_plan);
    if (ctx.is_pro !== undefined) id.set('is_pro', ctx.is_pro);
    if (ctx.credits_remaining !== undefined) id.set('credits_remaining', ctx.credits_remaining);
    if (ctx.user_email) id.set('email', ctx.user_email);
    if (ctx.ab_group !== undefined && ctx.ab_group !== null) {
      id.set('ab_group', ctx.ab_group);
    }
    // Always set, so a routing change shows up on the next user refresh.
    id.set('video_provider', analyticsProviderName(ctx.video_provider));
    id.set('locale', i18n.language || 'en');
    id.set('platform', 'web');
    // setOnce — only the very first login wins.
    id.setOnce('signup_date', new Date().toISOString());
    id.setOnce('signup_platform', 'web');

    identify(id);
  } catch (e) {
    console.error('[Amplitude] identify failed:', e);
  }
};

/**
 * Rotate the Amplitude device_id so subsequent events are anonymous again.
 * Called on logout.
 */
export const resetAmplitudeUser = () => {
  if (!initialized) return;
  try {
    reset();
  } catch (e) {
    console.error('[Amplitude] reset failed:', e);
  }
};

// ============================================
// CORE TRACK
// ============================================

/**
 * OWNER-APPROVED EVENT ALLOWLIST (2026-07-06). Amplitude receives ONLY these
 * event names — anything else is dropped HERE, at the single choke point,
 * regardless of which helper or legacy call site fires it. To add an event,
 * add it to this set deliberately; do not bypass trackAmplitude.
 * (Server-side events — User Created, Subscription Started, etc. — are sent
 * by the backend lambdas and are not governed by this list.)
 */
const KEPT_EVENTS = new Set([
  'App Opened',
  'Signup Started',
  'Logged In',
  'Generation Requested',
  'Plan Ready',
  'Free Chat Limit Reached',
  'Plan Card Generate Button Clicked',
  'Video Preview Ready',
  'Paywall CTA Clicked',
  'Paywall Viewed',
  'Checkout Started',
  // Checkout drop-off + recovery/exit offer funnel (previously dropped by this
  // allowlist, which hid why the offer works). Judge the offer by subs-per-viewer
  // and week-2 revenue, not by these click rates alone.
  'Checkout Canceled',
  'Exit Offer Shown',
  'Exit Offer Clicked',
  'Exit Offer Dismissed',
  'Exit Offer Expired',
  // Recovery-email offer ($4.99 / $1.99 first week) shown on the paywall.
  'Email Offer Viewed',
  'Avatar Created',
  'Voice Created',
  'Video Downloaded',
  'Video Rated',
  'Video Shared',
  'Provider Fallback',
  // Weekly → yearly nudges (owner 2026-10-29: "take inspiration from V2"): V1's annual-push names, fired by V3's Account
  // card and short-balance top-up sheet (src/v3/analytics/v3Events.ts, with `surface`, `plan`, `period`). V1, the only
  // other caller (src/features/account/AccountPage.tsx), is not served (resolveUiVersion always answers 'v2').
  'Annual Push Viewed',
  'Annual Push Clicked',
  'Annual Push Dismissed',
  // The upgrade page's Weekly/Yearly switch (owner 2026-10-30, upgrade-page Batch 1): V2's UpgradePage and V3's /upgrade
  // both call trackBillingPeriodToggle {new_period, ab_variant}, but the name was never kept, so neither reached Amplitude.
  'Billing Period Toggled',
]);

/**
 * Clean device bucket (mobile / tablet / desktop / unknown) from the user agent.
 * Mirrors the backend detect_device_type EXACTLY (mobile → tablet/ipad → desktop)
 * so this client value matches the `device_type` sent on the server-side
 * `User Created` and `Subscription Started` events — one consistent property to
 * group any funnel by, instead of Amplitude's granular built-in Device Type.
 * Computed once (the UA is stable for the session).
 */
const DEVICE_TYPE: 'mobile' | 'tablet' | 'desktop' | 'unknown' = (() => {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    if (!ua) return 'unknown';
    if (ua.includes('mobile')) return 'mobile';
    if (ua.includes('tablet') || ua.includes('ipad')) return 'tablet';
    return 'desktop';
  } catch {
    return 'unknown';
  }
})();

/**
 * Low-level track. Prefer the typed helpers below over calling this directly.
 * Automatically attaches global properties (platform, device_type, app_version,
 * locale, environment) to every event.
 */
export const trackAmplitude = (eventName: string, props: Record<string, unknown> = {}) => {
  if (!initialized) return;
  if (!KEPT_EVENTS.has(eventName)) return; // owner allowlist — see above
  try {
    track(eventName, {
      ...props,
      platform: 'web',
      device_type: DEVICE_TYPE,
      app_version: APP_VERSION,
      locale: i18n.language || 'en',
      environment: ENVIRONMENT,
    });
  } catch (e) {
    console.error('[Amplitude] track failed:', e);
  }
};

// ============================================
// AUTHENTICATION
// ============================================

type AuthMethod = 'google' | 'apple' | 'microsoft' | 'email' | 'bypass';

/**
 * App session opened — fired ONCE PER TAB SESSION at boot (V1 and V2 alike;
 * main.tsx runs before the version fork). The webapp-arrival denominator for
 * the landing→app and app→signup steps, and the DAU/active-users baseline
 * (without it, only users who generate would count as "active").
 */
export const trackAmpAppOpened = () => {
  // Public /share/:id links boot this same bundle, but a share viewer is not a
  // product arrival — counting them would inflate App Opened with viral traffic.
  if (window.location.pathname.startsWith('/share/')) return;
  try {
    if (sessionStorage.getItem('vivideo_app_opened')) return;
    sessionStorage.setItem('vivideo_app_opened', '1');
  } catch { /* private mode — fire every load rather than never */ }
  // is_authenticated is derived at boot from the stored backend user id (real
  // auth resolves later, after this fires). false ⇒ this tab is headed to the
  // /login page, so "App Opened where is_authenticated=false" is the
  // login-page-arrival cohort regardless of which path the tab opened on.
  let isAuthenticated = false;
  try { isAuthenticated = !!localStorage.getItem('vivideo_user_id'); } catch { /* ignore */ }
  trackAmplitude('App Opened', {
    initial_route: window.location.pathname,
    is_authenticated: isAuthenticated,
  });
};

/** Auth CTA pressed (before the provider round-trip) — measures signup intent
 *  vs `User Signed Up`/`User Logged In`, which only fire after auth succeeds. */
export const trackAmpSignupStarted = (method: AuthMethod) => {
  trackAmplitude('Signup Started', { method });
};

export const trackAmpUserSignedUp = (method: AuthMethod) => {
  trackAmplitude('User Signed Up', { method });
};

export const trackAmpUserLoggedIn = (method: AuthMethod, abGroup?: number) => {
  // Owner taxonomy: existing-user login → 'Logged In' {ab_group}. (New accounts
  // are covered by the server-side 'User Created'.)
  trackAmplitude('Logged In', { method, ab_group: abGroup });
};

export const trackAmpUserLoggedOut = () => {
  trackAmplitude('User Logged Out', {});
};

// ============================================
// NAVIGATION / DISCOVERY
// ============================================

export const trackAmpPageViewed = (pagePath: string, pageTitle?: string) => {
  trackAmplitude('Page Viewed', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

export const trackAmpExamplesViewed = () => {
  trackAmplitude('Examples Viewed', {});
};

export const trackAmpUpgradeBannerClicked = (location: string) => {
  trackAmplitude('Upgrade Banner Clicked', { banner_location: location });
};

// ============================================
// CREATION INTENT (HomePage)
// ============================================

export const trackAmpOperationSelected = (operation: string) => {
  trackAmplitude('Operation Selected', { operation });
};

export const trackAmpModelSelected = (params: {
  modelId: string;
  modelName?: string;
  operation?: string;
}) => {
  trackAmplitude('Model Selected', {
    model_id: params.modelId,
    model_name: params.modelName,
    operation: params.operation,
  });
};

export const trackAmpGenerationRequested = (params: {
  operationType: string;
  modelId?: string;
  duration?: string;
  aspectRatio?: string;
  resolution?: string;
  hasImage?: boolean;
  imageCount?: number;
  /** Owner taxonomy — V1: 'manual' | 'auto'; V2: 'manual' | 'autogenerate' | 'agentic'. */
  mode?: string;
  /** Owner taxonomy: was the requester on the free tier at submit time. */
  freeUser?: boolean;
}) => {
  trackAmplitude('Generation Requested', {
    operation_type: params.operationType,
    model_id: params.modelId,
    duration: params.duration,
    aspect_ratio: params.aspectRatio,
    resolution: params.resolution,
    has_image: params.hasImage,
    image_count: params.imageCount,
    mode: params.mode,
    free_user: params.freeUser,
  });
};

/** V2 plan card's glowing Generate pressed (distinct from the SUBMIT, which is
 *  'Generation Requested'). Owner taxonomy. */
export const trackAmpPlanCardGenerateClicked = (mode: string) => {
  trackAmplitude('Plan Card Generate Button Clicked', { mode });
};

/** The fake "your video is ready" result rendered — V2 blurred gate (with mode)
 *  or V1 preview modal (no mode). Owner taxonomy: 'Video Preview Ready'. */
export const trackAmpVideoPreviewReady = (mode?: string) => {
  trackAmplitude('Video Preview Ready', mode ? { mode } : {});
};

export const trackAmpFirstGenerationRequested = (operationType: string) => {
  trackAmplitude('First Generation Requested', { operation_type: operationType });
};

// ============================================
// MONETIZATION
// ============================================

/** Owner taxonomy: paywall = landing on /subscription. Fires there only. */
export const trackAmpPaywallViewed = (context: string, extra?: Record<string, string>) => {
  trackAmplitude('Paywall Viewed', { paywall_context: context, ...extra });
};

/** A click that routes the user to the paywall (preview play/download/share/
 *  fullscreen, upgrade-popup CTA). `action` says which control. Owner taxonomy. */
export const trackAmpPaywallCtaClicked = (action: string) => {
  trackAmplitude('Paywall CTA Clicked', { action });
};

export const trackAmpCheckoutStarted = (planType: string, price: number) => {
  trackAmplitude('Checkout Started', {
    plan_type: planType,
    value: price,
    currency: 'USD',
  });
};

export const trackAmpCheckoutAbandoned = (extra?: Record<string, string>) => {
  // Fired on the ?canceled=true return from Stripe. Emitted as 'Checkout
  // Canceled' (in the KEPT_EVENTS allowlist) so the drop-off is measurable.
  trackAmplitude('Checkout Canceled', { ...extra });
};

// ============================================
// RECOVERY / EXIT OFFER
// ============================================

/** The $4.99-first-week offer became visible (overlay shown once on leave). */
export const trackAmpExitOfferShown = () => {
  trackAmplitude('Exit Offer Shown', {});
};

/** User tapped the offer CTA (overlay or persistent banner). */
export const trackAmpExitOfferClicked = (surface: 'overlay' | 'banner') => {
  trackAmplitude('Exit Offer Clicked', { surface });
};

/** User closed the overlay (the offer continues in the banner until it expires). */
export const trackAmpExitOfferDismissed = () => {
  trackAmplitude('Exit Offer Dismissed', {});
};

/** The 10-minute deadline elapsed with no purchase. */
export const trackAmpExitOfferExpired = () => {
  trackAmplitude('Exit Offer Expired', {});
};

/** Recovery-email offer shown live on the paywall (once per page load). */
export const trackAmpEmailOfferViewed = (offer: string, priceCents: number, expiresAt: string) => {
  trackAmplitude('Email Offer Viewed', { offer, price_cents: priceCents, expires_at: expiresAt });
};

export const trackAmpBillingPeriodToggled = (
  newPeriod: 'weekly' | 'monthly' | 'yearly',
  variant: string
) => {
  trackAmplitude('Billing Period Toggled', {
    new_period: newPeriod,
    ab_variant: variant,
  });
};

export const trackAmpCancelReasonSelected = (reason: string) => {
  trackAmplitude('Cancel Reason Selected', { cancel_reason: reason });
};

/**
 * Client-side purchase echo. The backend is the source of truth for revenue
 * (`Subscription Started` / `Credits Purchased` are fired from stripe-webhook
 * with `$revenue`), so this client event is an intent/UX signal only — we
 * don't attach `$revenue` here to avoid double-counting in Amplitude's
 * Revenue chart.
 */
export const trackAmpPurchaseCompleted = (params: {
  planType: string;
  value: number;
  credits?: number;
  isSubscription: boolean;
  transactionId: string;
}) => {
  trackAmplitude('Purchase Completed', {
    transaction_id: params.transactionId,
    plan_type: params.planType,
    value: params.value,
    currency: 'USD',
    credits_purchased: params.credits,
    is_subscription: params.isSubscription,
    transaction_type: params.isSubscription ? 'subscription' : 'credits',
  });
};

// Optional helper (not currently used) for attaching client-side revenue to a
// purchase if we ever want to stop relying on server-side webhooks.
export const trackAmpRevenueClient = (params: {
  price: number;
  quantity?: number;
  productId: string;
  revenueType?: string;
}) => {
  if (!initialized) return;
  try {
    const r = new Revenue()
      .setPrice(params.price)
      .setQuantity(params.quantity ?? 1)
      .setProductId(params.productId);
    if (params.revenueType) r.setRevenueType(params.revenueType);
    amplitudeRevenue(r);
  } catch (e) {
    console.error('[Amplitude] revenue track failed:', e);
  }
};

// ============================================
// ACCOUNT PAGE CLICKS
// ============================================

export const trackAmpCreditsClicked = (params: { credits: number; is_pro: boolean }) => {
  trackAmplitude('Credits Clicked', params);
};

export const trackAmpSubscriptionClicked = (params: { user_plan: string; is_pro: boolean }) => {
  trackAmplitude('Subscription Clicked', params);
};

export const trackAmpOurPlansClicked = (params: { is_pro: boolean }) => {
  trackAmplitude('Our Plans Clicked', params);
};

export const trackAmpManageSubscriptionClicked = (params: {
  user_plan: string;
  is_pro: boolean;
  has_active_sub: boolean;
}) => {
  trackAmplitude('Manage Subscription Clicked', params);
};

export const trackAmpRateUsClicked = () => {
  trackAmplitude('Rate Us Clicked', {});
};

export const trackAmpDeleteAccountClicked = (params: {
  stage: 'prompt' | 'confirmed' | 'canceled';
  user_plan?: string;
}) => {
  trackAmplitude('Delete Account Clicked', params);
};

type SocialPlatform = 'website' | 'instagram' | 'facebook' | 'linkedin' | 'x' | 'youtube';
export const trackAmpSocialLinkClicked = (platform: SocialPlatform) => {
  trackAmplitude('Social Link Clicked', { platform });
};

type LegalLinkType = 'about' | 'privacy' | 'terms';
export const trackAmpLegalLinkClicked = (link_type: LegalLinkType) => {
  trackAmplitude('Legal Link Clicked', { link_type });
};

// ============================================
// SUBSCRIPTION LIFECYCLE CLICKS (cancel flow + retention)
// ============================================

type CancelFlowStep =
  | 'menu'
  | 'lose_benefits'
  | 'reason_select'
  | 'pause_offer'
  | 'free_week_offer'
  | 'yearly_bonus_offer'
  | 'contact_email';
export const trackAmpCancelFlowStepViewed = (params: {
  step: CancelFlowStep;
  user_plan: string;
  billing_period?: string;
}) => {
  trackAmplitude('Cancel Flow Step Viewed', params);
};

export const trackAmpCancelFlowExited = (params: {
  step: CancelFlowStep;
  exit_action: 'keep_subscription' | 'close_modal';
  user_plan: string;
}) => {
  trackAmplitude('Cancel Flow Exited', params);
};

// 'pause' and 'bonus_credits' are offered by the V2 cancellation funnel
// (Settings → Manage Subscription); the rest are legacy offers.
type RetentionOffer = 'pause' | 'free_week' | 'yearly_bonus' | 'bonus_credits';
export const trackAmpRetentionOfferAccepted = (params: {
  offer: RetentionOffer;
  user_plan: string;
  billing_period?: string;
}) => {
  trackAmplitude('Retention Offer Accepted', params);
};

export const trackAmpRetentionOfferDeclined = (params: {
  offer: RetentionOffer;
  user_plan: string;
  billing_period?: string;
}) => {
  trackAmplitude('Retention Offer Declined', params);
};

export const trackAmpRefundRequested = (params: { stage: 'policy_viewed' | 'email_shown' }) => {
  trackAmplitude('Refund Requested', params);
};

// ============================================
// UPGRADE / PLAN SWITCH PROMPTS
// ============================================

export const trackAmpAnnualPushViewed = (params: { user_plan: string; billing_period: string }) => {
  trackAmplitude('Annual Push Viewed', params);
};

export const trackAmpAnnualPushClicked = (params: { user_plan: string; billing_period: string }) => {
  trackAmplitude('Annual Push Clicked', params);
};

export const trackAmpAnnualPushDismissed = (params: {
  user_plan: string;
  billing_period: string;
}) => {
  trackAmplitude('Annual Push Dismissed', params);
};

export const trackAmpSwitchToYearlyClicked = (params: {
  source: 'annual_push' | 'cancel_flow';
  user_plan: string;
  billing_period: string;
}) => {
  trackAmplitude('Switch To Yearly Clicked', params);
};

// ============================================
// ENGAGEMENT
// ============================================

export const trackAmpVideoPlayed = (operationType: string) => {
  trackAmplitude('Video Played', { operation_type: operationType });
};

/** `extra` is additive — call sites that know the order/provider add them; the
 *  existing `operation_type` payload is unchanged for everyone else. */
export const trackAmpVideoDownloaded = (operationType: string, extra?: Record<string, unknown>) => {
  // `provider` is normalised last so every call site reports one of the three A/B names.
  const { approved_at, ...rest } = extra ?? {};
  trackAmplitude('Video Downloaded', {
    operation_type: operationType,
    ...rest,
    provider: analyticsProviderName(extra?.provider as string | null | undefined),
    fallback_from: analyticsFallbackFrom(extra?.fallback_from as string | null | undefined),
    ...analyticsVideoTime(approved_at as string | null | undefined),
  });
};

/** Thumbs up/down on a finished video — one vote per delivered file. */
export const trackAmpVideoRated = (params: {
  rating: 'like' | 'dislike';
  orderId: string;
  provider?: string | null;
  /** The order's fallback mark, when HeyGen made the video in a routed provider's place. */
  fallbackFrom?: string | null;
  /** The order's `approved_at` — sent as video_approved_ts / video_age_h. */
  approvedAt?: string | null;
  videoDurationS?: number | null;
}) => {
  trackAmplitude('Video Rated', {
    rating: params.rating,
    order_id: params.orderId,
    provider: analyticsProviderName(params.provider),
    fallback_from: analyticsFallbackFrom(params.fallbackFrom),
    video_duration_s: typeof params.videoDurationS === 'number' ? Math.round(params.videoDurationS) : undefined,
    ...analyticsVideoTime(params.approvedAt),
  });
};

/** `extra` is additive — call sites add share_method / order_id / provider /
 *  platform where they know them; `operation_type` is unchanged for everyone else.
 *  `extra.operation_type` overrides it for AMPLITUDE ONLY (it is spread last), so a
 *  social publish can report 'video' here while GA keeps sending the platform. */
export const trackAmpVideoShared = (operationType: string, extra?: Record<string, unknown>) => {
  // `provider` is normalised last so every call site reports one of the three A/B names.
  const { approved_at, ...rest } = extra ?? {};
  trackAmplitude('Video Shared', {
    operation_type: operationType,
    ...rest,
    provider: analyticsProviderName(extra?.provider as string | null | undefined),
    fallback_from: analyticsFallbackFrom(extra?.fallback_from as string | null | undefined),
    ...analyticsVideoTime(approved_at as string | null | undefined),
  });
};

/** A LongVid / VideoGen create that HeyGen took over — the order carries `fallback_from`.
 *  Fired once per project when the page first sees the mark, so the A/B can count how
 *  often an arm's traffic actually landed on HeyGen, and why. */
export const trackAmpProviderFallback = (params: {
  orderId: string;
  from?: string | null;
  reason?: string | null;
  mode: 'auto' | 'agent';
}) => {
  trackAmplitude('Provider Fallback', {
    order_id: params.orderId,
    from: analyticsFallbackFrom(params.from),
    to: 'heygen',
    reason: params.reason || undefined,
    mode: params.mode,
  });
};

export const trackAmpVideoRemixStarted = (originalOperationType: string) => {
  trackAmplitude('Video Remix Started', {
    original_operation_type: originalOperationType,
  });
};

export const trackAmpFullscreenBlocked = () => {
  trackAmplitude('Fullscreen Blocked', {});
};

// ============================================
// FILES
// ============================================

export const trackAmpFolderCreated = () => {
  trackAmplitude('Folder Created', {});
};

export const trackAmpFileRenamed = () => {
  trackAmplitude('File Renamed', {});
};

export const trackAmpFileMoved = () => {
  trackAmplitude('File Moved', {});
};

export const trackAmpFileDeleted = (operationType?: string) => {
  trackAmplitude('File Deleted', { operation_type: operationType });
};

// ============================================
// V2 FEATURE USAGE (new in V2 — no V1 equivalent)
// Adoption metrics for the redesign's net-new flows: asset creation + templates.
// Amplitude-only (product analytics); GA/Meta are reserved for ads-conversion events.
// ============================================

/**
 * A project/order came into existence from a user submit — the anchor for
 * mode-usage rates and the "demo started" step of the free funnel. Fires once
 * per successful create in ALL three modes (agent chat submit, auto submit,
 * manual create), free and pro alike.
 */
export const trackAmpProjectCreated = (params: { mode: 'agent' | 'auto' | 'manual'; free: boolean }) => {
  trackAmplitude('Project Created', { mode: params.mode, free_user: params.free });
};

/**
 * The FIRST plan card landed in an agent project — with how long the user actually
 * waited (ms from entering the planning state to the plan appearing, i.e. only while
 * they stuck around). THE metric for the fast free planner: Project Created →
 * Plan Ready → generation_started shows exactly where the plan-wait bounce happens.
 */
export const trackAmpPlanReady = (params: { waitMs: number }) => {
  trackAmplitude('Plan Ready', { wait_ms: Math.round(params.waitMs) });
};

/** Free chat cap hit (composer parked with the Generate steer) — once per project. */
export const trackAmpFreeChatLimitReached = () => {
  trackAmplitude('Free Chat Limit Reached', {});
};

/** A file upload SUCCEEDED (composer attach, assets page, or picker modal). */
export const trackAmpAssetUploaded = (params: { source: string; fileType?: string; sizeBucket?: string }) => {
  // source: 'home_composer' | 'assets_page' | 'file_picker'
  trackAmplitude('Asset Uploaded', {
    source: params.source,
    file_type: params.fileType,
    size_bucket: params.sizeBucket,
  });
};

/** Bucket a file size for analytics (avoid raw byte counts as event props). */
export const assetSizeBucket = (bytes: number): string => {
  if (bytes < 1024 * 1024) return '<1MB';
  if (bytes < 10 * 1024 * 1024) return '1-10MB';
  if (bytes < 50 * 1024 * 1024) return '10-50MB';
  return '50MB+';
};

export const trackAmpAvatarCreated = (params: { kind: string }) => {
  // kind: 'prompt' (AI) | 'photo' | 'digital_twin'
  trackAmplitude('Avatar Created', { avatar_kind: params.kind });
};

export const trackAmpVoiceCreated = (params: { method: 'clone' | 'design' }) => {
  trackAmplitude('Voice Created', { voice_method: params.method });
};

export const trackAmpBrandKitCreated = () => {
  trackAmplitude('Brand Kit Created', {});
};

export const trackAmpTemplateApplied = (params: { templateId: string; templateName?: string }) => {
  trackAmplitude('Template Applied', {
    template_id: params.templateId,
    template_name: params.templateName,
  });
};

// Asset ENGAGEMENT (distinct from creation): attaching an asset to a video, and deletes.
export const trackAmpAssetSelected = (params: { assetType: string; source: string }) => {
  // assetType: 'avatar' | 'voice' | 'brand'; source: 'picker' | 'assets_page'
  trackAmplitude('Asset Selected', { asset_type: params.assetType, source: params.source });
};

export const trackAmpAssetDeleted = (params: { assetType: string }) => {
  // assetType: 'avatar' | 'voice' | 'brand' | 'file'
  trackAmplitude('Asset Deleted', { asset_type: params.assetType });
};

// ============================================
// SETTINGS / SUPPORT
// ============================================

export const trackAmpLanguageChanged = (newLanguage: string) => {
  trackAmplitude('Language Changed', { new_language: newLanguage });
};

export const trackAmpAppShared = () => {
  trackAmplitude('App Shared', {});
};

export const trackAmpBugReported = () => {
  trackAmplitude('Bug Reported', {});
};

export const trackAmpHelpRequested = () => {
  trackAmplitude('Help Requested', {});
};

// ============================================
// ERRORS
// ============================================

export const trackAmpErrorShown = (errorType: string, errorMessage?: string) => {
  trackAmplitude('Error Shown', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

const amplitudeService = {
  init: initAmplitude,
  identify: identifyAmplitudeUser,
  reset: resetAmplitudeUser,
  track: trackAmplitude,
};

export default amplitudeService;
